import { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useGetReleaseKitQuery, useGetReleaseKitsQuery } from '../../../../../redux/api/fymAPI';
import { CatalogItem } from '../../../../../redux/api/fymAPI.types';
import { PromoteMediaDraft } from '../../draftStore';
import { revisionCardLabel } from '../../releases/assets/cardLabel';
import { defaultPickerSong, filterPickerFiles, mediaDraftOfPickerFile, PickerFilter, pickerFiles } from './pickerFiles';
import { usePickerUpload } from './usePickerUpload';
import { useTRPCAPI, useTRPCQuery } from '../../../../../trpc/trpc';
import { filterPickerUploads, mediaDraftOfUploadFile, uploadPickerKey } from './pickerUploads';

/** The newest uploads that the picker shows ("Your uploads", decision 8 of the assets rethink). */
const PICKER_UPLOADS_LIMIT = 24;

/** The key of the handed-over file in the selection. */
export const HANDOVER_KEY = 'handover';

interface UseComposerMediaPickerProps {
  accountId: number;
  items: CatalogItem[];
  /** The release that the Composer already picked. */
  releaseUuid: string | undefined;
  handover: PromoteMediaDraft | undefined;
  onAttach: (media: PromoteMediaDraft) => void;
}

/**
 * The state of the media picker: the song (the Composer release, else the newest song with a ready
 * file), the filter, the files of the song, the selection and the upload.
 *
 * The picker body mounts when the dialog opens and unmounts when it closes
 * (`ComposerAttachMediaModal`), so every open starts from the Composer release with fresh state:
 * no song, filter, selection or upload of an earlier open is left, not even for the first render.
 */
export function useComposerMediaPicker({
  accountId,
  items,
  releaseUuid,
  handover,
  onAttach,
}: UseComposerMediaPickerProps) {
  const intl = useIntl();
  const [pickedSong, setPickedSong] = useState<string | undefined>();
  const [hasPickedSong, setHasPickedSong] = useState(false);
  const [filter, setFilter] = useState<PickerFilter>('all');
  const [selectedKey, setSelectedKey] = useState<string | undefined>(() =>
    handover === undefined ? undefined : HANDOVER_KEY
  );

  // `include: 'assets'` tells which song has a postable file (`defaultPickerSong`).
  const kits = useGetReleaseKitsQuery({ accountId, include: 'assets' }, { skip: releaseUuid !== undefined });
  const song = hasPickedSong ? pickedSong : defaultPickerSong(releaseUuid, kits.data?.kits);
  const item = useMemo(() => items.find((one) => one.uuid === song), [items, song]);
  const kit = useGetReleaseKitQuery({ accountId, catalogItemUuid: song ?? '' }, { skip: song === undefined });

  const allFiles = useMemo(() => (song === undefined ? [] : pickerFiles(kit.data)), [kit.data, song]);
  const files = useMemo(() => filterPickerFiles(allFiles, filter), [allFiles, filter]);

  // design: assets-rethink a1, decision 8. The photos and videos that the artist brought.
  const trpcAPI = useTRPCAPI();
  const uploadsQuery = useTRPCQuery(
    trpcAPI.assets.uploads.queryOptions({ accountId, limit: PICKER_UPLOADS_LIMIT }, { retry: false })
  );
  const uploads = useMemo(
    () => filterPickerUploads(uploadsQuery.data?.items ?? [], filter),
    [filter, uploadsQuery.data]
  );

  // Attaching a file stops an upload that still runs: its result must not replace this choice.
  const upload = usePickerUpload({ accountId, onUploaded: onAttach });
  const { reset: resetUpload } = upload;

  const changeSong = useCallback((uuid: string | undefined) => {
    setPickedSong(uuid);
    setHasPickedSong(true);
    setSelectedKey((current) => (current === HANDOVER_KEY ? current : undefined));
  }, []);

  /** A filter that hides the selected file clears the selection: Attach never takes a hidden file. */
  const changeFilter = useCallback(
    (next: PickerFilter) => {
      setFilter(next);
      const visible = filterPickerFiles(allFiles, next);
      const visibleUploads = filterPickerUploads(uploadsQuery.data?.items ?? [], next);
      setSelectedKey((current) =>
        current === undefined ||
        current === HANDOVER_KEY ||
        visible.some((one) => one.key === current) ||
        visibleUploads.some((one) => uploadPickerKey(one) === current)
          ? current
          : undefined
      );
    },
    [allFiles, uploadsQuery.data]
  );

  const attachKey = useCallback(
    (key: string | undefined) => {
      if (key === HANDOVER_KEY && handover !== undefined) {
        resetUpload();
        onAttach(handover);
        return;
      }
      const file = files.find((one) => one.key === key);
      if (file !== undefined) {
        resetUpload();
        onAttach(mediaDraftOfPickerFile(file, item, revisionCardLabel(intl, file.card)));
        return;
      }
      const earlier = uploads.find((one) => uploadPickerKey(one) === key);
      if (earlier !== undefined) {
        resetUpload();
        onAttach(mediaDraftOfUploadFile(earlier));
      }
    },
    [files, handover, intl, item, onAttach, resetUpload, uploads]
  );

  const attachSelected = useCallback(() => attachKey(selectedKey), [attachKey, selectedKey]);

  const canAttach =
    (selectedKey === HANDOVER_KEY && handover !== undefined) ||
    files.some((one) => one.key === selectedKey) ||
    uploads.some((one) => uploadPickerKey(one) === selectedKey);

  return {
    song,
    item,
    changeSong,
    filter,
    setFilter: changeFilter,
    allFiles,
    files,
    isLoading: (song === undefined && kits.isLoading) || (song !== undefined && kit.isLoading),
    error: song === undefined ? kits.error : kit.error,
    refetch: song === undefined ? kits.refetch : kit.refetch,
    selectedKey,
    select: setSelectedKey,
    attachKey,
    attachSelected,
    canAttach,
    upload,
    uploads,
  };
}

export type ComposerMediaPicker = ReturnType<typeof useComposerMediaPicker>;
